// src/display.ts
// Display settings for comfort and accessibility. Saved in this browser
// only (they're about this device, not your account) and applied as
// classes on <html>, which styles.css picks up.
export type DisplayPrefs = {
  readableFont: boolean; // Atkinson Hyperlegible everywhere
  largerText: boolean;
  calm: boolean; // no marquee, blinking, falling envelopes, or glitter
  plainBackground: boolean;
  plainCursor: boolean; // the computer's own cursor instead of the pixel ones
  darkBackground: boolean; // a dark desk behind the windows
};

const KEY = "penpal-display";

export const DISPLAY_OPTIONS: { key: keyof DisplayPrefs; label: string; hint: string }[] = [
  { key: "readableFont", label: "Easy-read font", hint: "Swaps the handwriting and pixel fonts for one designed for low vision and dyslexia." },
  { key: "largerText", label: "Bigger text", hint: "Makes everything a bit larger." },
  { key: "calm", label: "Calm mode", hint: "Stops the scrolling banner, blinking, glitter, and other motion." },
  { key: "plainBackground", label: "Plain background", hint: "Removes the patterned background." },
  { key: "darkBackground", label: "Dark background", hint: "A dark desk behind the windows. Easier on the eyes at night or with light sensitivity." },
  { key: "plainCursor", label: "Regular cursor", hint: "Uses your computer's own mouse pointer instead of the pixel rainbow ones." },
];

const CLASS: Record<keyof DisplayPrefs, string> = {
  readableFont: "pref-readable",
  largerText: "pref-larger",
  calm: "pref-calm",
  plainBackground: "pref-plain",
  plainCursor: "pref-cursor",
  darkBackground: "pref-dark",
};

export function loadDisplay(): DisplayPrefs {
  const none = { readableFont: false, largerText: false, calm: false, plainBackground: false, plainCursor: false, darkBackground: false };
  try {
    return { ...none, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return none;
  }
}

export function applyDisplay(prefs: DisplayPrefs) {
  (Object.keys(CLASS) as (keyof DisplayPrefs)[]).forEach((k) =>
    document.documentElement.classList.toggle(CLASS[k], !!prefs[k])
  );
}

export function saveDisplay(prefs: DisplayPrefs) {
  applyDisplay(prefs);
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable: the settings still apply for this visit
  }
}
